// Shared behaviour for the "/" variable suggest list shown by the editors that render variables as
// chips. The host supplies the `variables` prop and implements `applySuggestSelection(item)` - the
// only part that differs between the contenteditable field and the Quill editor, since replacing the
// typed "/query" text with a chip is done differently on each surface - plus `refreshSuggestPosition()`,
// which re-measures the caret so the list follows it when the page resizes or scrolls.
//
// Everything else lives here so both surfaces filter, highlight and dismiss identically.
export default {
    data() {
        return {
            suggestVisible: false,
            suggestItems: [],
            suggestIndex: 0,
            suggestRect: null,
            suggestQuery: "",
        };
    },
    computed: {
        suggestActiveItem() {
            return this.suggestItems[this.suggestIndex] ?? null;
        },
    },
    beforeDestroy() {
        this.removeSuggestViewportListeners();
    },
    methods: {
        // the list is fixed to the caret, so it is re-anchored while it is open - the window listeners
        // only exist for that window, so editors that never open the list register nothing global
        addSuggestViewportListeners() {
            window.addEventListener("resize", this.handleSuggestViewportChange);
            window.addEventListener("scroll", this.handleSuggestViewportChange, true);
        },
        removeSuggestViewportListeners() {
            window.removeEventListener("resize", this.handleSuggestViewportChange);
            window.removeEventListener("scroll", this.handleSuggestViewportChange, true);
        },
        // case-insensitive substring match on the label or the id, so "/deliv" narrows to "Delivery Date"
        filterSuggestVariables(query) {
            const needle = (query ?? "").toLowerCase();

            if (!needle) {
                return this.variables ?? [];
            }

            return (this.variables ?? []).filter((variable) => {
                return (
                    (variable.label ?? "").toLowerCase().includes(needle) ||
                    (variable.id ?? "").toLowerCase().includes(needle)
                );
            });
        },
        // rect is the caret's viewport position - { left, bottom } - so the list opens under the caret
        openSuggest(query, rect) {
            const items = this.filterSuggestVariables(query);

            if (!items.length) {
                this.closeSuggest();
                return;
            }

            // only the position changed (a resize or scroll) - keep the highlighted row where it was
            const sameQuery = this.suggestVisible && query === this.suggestQuery;

            this.suggestItems = items;
            this.suggestQuery = query;
            this.suggestRect = rect;
            this.suggestVisible = true;

            if (sameQuery) {
                this.suggestIndex = Math.min(this.suggestIndex, items.length - 1);
            } else {
                this.suggestIndex = 0;
            }

            this.addSuggestViewportListeners();
        },
        closeSuggest() {
            this.removeSuggestViewportListeners();
            this.suggestVisible = false;
            this.suggestItems = [];
            this.suggestIndex = 0;
            this.suggestRect = null;
            this.suggestQuery = "";
        },
        // re-measure the caret while the list is open, so a resize or scroll cannot leave it stranded
        handleSuggestViewportChange(event) {
            // the list scrolls its own options - that does not move the caret it is anchored to
            if (event?.type === "scroll" && event.target instanceof Element && event.target.closest?.(".bo-variable-suggest")) {
                return;
            }

            if (this.suggestVisible) {
                this.refreshSuggestPosition();
            }
        },
        moveSuggest(step) {
            const count = this.suggestItems.length;

            if (count) {
                this.suggestIndex = (this.suggestIndex + step + count) % count;
            }
        },
        // Returns true when the key belongs to the open list, so the host can also preventDefault.
        handleSuggestKeydown(event) {
            if (!this.suggestVisible) {
                return false;
            }

            switch (event.key) {
                case "ArrowDown":
                    this.moveSuggest(1);
                    return true;
                case "ArrowUp":
                    this.moveSuggest(-1);
                    return true;
                case "Enter":
                case "Tab":
                    if (this.suggestActiveItem) {
                        this.selectSuggestItem(this.suggestActiveItem);
                    }
                    return true;
                case "Escape":
                    this.closeSuggest();
                    return true;
                default:
                    return false;
            }
        },
        selectSuggestItem(item) {
            this.applySuggestSelection(item);
            this.closeSuggest();
        },
    },
};
